import React, { useState, useEffect } from 'react';
import {
  PageHeader,
  Button,
  Layout,
  Spin,
  Empty,
  Image,
  Table,
  Tag,
  Dropdown,
  Menu,
  Popconfirm,
  Space,
} from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import ModalUpsertRecipe from '../components/ModalUpsertRecipe';
import { recipesQuery, deleteRecipeMutation } from '../graphql/recipe';
import { ITEMS_PER_PAGE, resourceUrl, Routes } from '../constants';
import useQueryParams from '../components/useQueryParams';
import { useHistory } from 'react-router-dom';

const Recipes = () => {
  const [upsertModalOpen, setUpsertModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [totalRecipes, setTotalRecipes] = useState([]);
  const queryParams = useQueryParams();
  const history = useHistory();

  const currentPage = queryParams.get('page') || 1;

  const { data, loading: recipesLoading, refetch } = useQuery(recipesQuery, {
    variables: {
      limit: ITEMS_PER_PAGE,
      offset: ITEMS_PER_PAGE * (currentPage - 1),
    },
  });
  const [deleteRecipe] = useMutation(deleteRecipeMutation);

  useEffect(() => {
    if (data?.recipes?.rows) {
      setRecipes(data.recipes.rows);
      setTotalRecipes(data.recipes.count);
    }
  }, [data]);

  const openModal = (selectedItem = null) => {
    setUpsertModalOpen(true);
    setSelectedItem(selectedItem);
  };

  const closeModal = (shouldRedirect) => {
    setUpsertModalOpen(false);
    setSelectedItem(null);

    // redirect to first page if new recipe insert
    if (shouldRedirect) {
      history.push({
        pathname: Routes.RECIPES,
      });
    }

    // refetch data on close modal
    refetch();
  };

  return (
    <Spin spinning={recipesLoading}>
      {upsertModalOpen && (
        <ModalUpsertRecipe
          modalOpen={upsertModalOpen}
          selectedItem={selectedItem}
          closeModal={closeModal}
        />
      )}

      <PageHeader
        className="page-header"
        title="Recipes"
        extra={
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => openModal()}
          >
            Add Recipe
          </Button>
        }
      />

      <Layout.Content className="page-content">
        {recipes.length === 0 && <Empty />}

        {recipes.length > 0 && (
          <Table
            bordered
            size="small"
            rowKey={(record) => record.id}
            pagination={{
              total: totalRecipes,
              pageSize: ITEMS_PER_PAGE,
              current: parseInt(currentPage, 10),
              defaultCurrent: 1,
              onChange: (page) => {
                history.push({
                  pathname: Routes.RECIPES,
                  search: `?page=${page}`,
                });
              },
            }}
            columns={[
              {
                title: '#',
                key: 'id',
                align: 'center',
                render: (_text, _record, index) => {
                  return index + 1;
                },
              },
              {
                title: 'Title',
                dataIndex: 'translation',
                key: 'translation',
                width: '100%',
                render: ({ title }) => title,
              },
              {
                title: 'Image',
                dataIndex: 'imagePath',
                key: 'imagePath',
                render: (imagePath) => (
                  <Image
                    src={`${resourceUrl}/${imagePath}`}
                    style={{ cursor: 'pointer' }}
                  />
                ),
              },
              {
                title: 'Tags',
                dataIndex: 'tags',
                key: 'tags',
                align: 'center',
                render: (tags) => (
                  <Space>
                    {tags.map(({ id, name }) => (
                      <Tag key={id} color="default">
                        #{name}
                      </Tag>
                    ))}
                  </Space>
                ),
              },
              {
                title: 'Action',
                key: 'action',
                align: 'center',
                render: (_text, record) => (
                  <Dropdown
                    overlay={
                      <Menu>
                        <Menu.Item onClick={() => openModal(record)}>
                          Edit
                        </Menu.Item>

                        <Menu.Divider />

                        <Menu.Item danger>
                          <Popconfirm
                            title="Really delete recipe?"
                            onConfirm={async () => {
                              await deleteRecipe({
                                variables: { id: record.id },
                              });

                              await refetch();
                            }}
                            okText="Yes"
                            cancelText="No"
                          >
                            Delete...
                          </Popconfirm>
                        </Menu.Item>
                      </Menu>
                    }
                    placement="bottomLeft"
                    arrow
                  >
                    <Button icon={<SettingOutlined />} />
                  </Dropdown>
                ),
              },
            ]}
            dataSource={recipes}
          />
        )}
      </Layout.Content>
    </Spin>
  );
};

export default Recipes;
